import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
// B22 — Bulk-clone moved from popup modal into an inline sub-tab on
// this page. The "Manage" tab shows the existing services list; the
// "Clone" tab shows the source→destination flow with the same logic
// but rendered inline. The old BulkCloneServicesModal component is
// no longer imported anywhere and can be deleted in a follow-up
// cleanup. (Keeping the file in this commit to keep the diff small.)
import CloneServicesPanel from '../../../../components/CloneServicesPanel/CloneServicesPanel';
import ServiceAddEditModal, {
    type ServiceFormData,
} from '../../../../components/ServiceAddEditModal/ServiceAddEditModal';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import { useServices } from '../../../../hooks/useServices';
import { useCategories } from '../../../../hooks/useCategories';
import { useBranches } from '../../../../hooks/useBranches';
import { useStaff } from '../../../../hooks/useStaff';
import { useToast } from '../../../../contexts/ToastContext';
import { useUser } from '../../../../contexts/UserContext';
import { queryKeys } from '../../../../lib/queryKeys';
import { resolveImageUrl } from '../../../../utils/imageUrl';
import GenderGlyph from '../../../../components/GenderGlyph';
import AgeGroupChip from '../../../../components/AgeGroupChip';
import type {
    ServiceDetailsResponse,
    Category,
    BranchDto,
    BusinessStaffDto,
} from '../../../../types/api-responses';
import styles from './Section.module.css';
import s from './ServicesSection.module.css';

/**
 * CR-2026-05-12 — Services sub-section (My Business shell).
 *
 * Mirrors the canonical Branches pattern (CR-2026-05-10/11): dark Fresha
 * tokens, list-of-cards layout, toolbar with count, polished empty state,
 * single-modal add/edit (no 3-step wizard), edit/delete inline.
 *
 * Reuses the existing /api/service endpoints via the `useServices` hook —
 * no BE change. On mutation we invalidate the TanStack-cached services list
 * so the My Business sidebar dot (driven by useBusinessSetupStatus) reflects
 * the new state immediately.
 *
 * Staff assignment is deferred to a follow-up PBI per CR-2026-05-12-v2 AC-5.
 * The modal preserves existing assignedStaffIds on edit (pass-through) so
 * salons that previously used the standalone /services wizard to assign
 * staff don't see their assignments wiped by an edit here.
 *
 * Not in scope here (sibling PBIs):
 *   - i18n key extraction (strings hardcoded; matches sibling My Business
 *     sections until the LanguageContext keys land in one batch)
 *   - Decision on the fate of the standalone /services route (keep / deprecate)
 *   - Migrating useServices to TanStack Query (currently manual fetch+useState)
 */

const ACTIVATION_THRESHOLD = 3;

interface ServiceRow extends ServiceDetailsResponse {
    /** Pretty-printed duration ("30 — 60 min") for the card body. */
    formattedDuration: string;
    /** Lowest price across all pricing options + currency ("from AED 150"). */
    formattedLowestPrice: string;
    /** Branch name resolved via the branch map. */
    branchName: string;
}

/** Strip a `HH:mm:ss` (or `HH:mm`) string down to a minute count. */
function durationToMinutes(value: string | undefined | null): number | null {
    if (!value) return null;
    const parts = value.split(':');
    if (parts.length < 2) return null;
    const hours = parseInt(parts[0], 10);
    const mins = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(mins)) return null;
    return hours * 60 + mins;
}

function formatMinutes(mins: number | null): string {
    if (mins === null) return '';
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

function toRow(
    dto: ServiceDetailsResponse,
    branchMap: Record<number, string>,
): ServiceRow {
    const minMins = durationToMinutes(dto.minDuration);
    const maxMins = durationToMinutes(dto.maxDuration);
    const formattedDuration =
        minMins !== null && maxMins !== null && minMins !== maxMins
            ? `${formatMinutes(minMins)} — ${formatMinutes(maxMins)}`
            : formatMinutes(maxMins ?? minMins);

    const prices = (dto.pricingOptions ?? [])
        .map(p => p.price)
        .filter(p => typeof p === 'number' && p > 0);
    const lowest = prices.length > 0 ? Math.min(...prices) : null;
    const currency = dto.pricingOptions?.[0]?.currency ?? 'AED';
    const formattedLowestPrice =
        lowest !== null
            ? prices.length > 1
                ? `from ${currency} ${lowest.toLocaleString()}`
                : `${currency} ${lowest.toLocaleString()}`
            : '—';

    return {
        ...dto,
        formattedDuration,
        formattedLowestPrice,
        branchName: dto.branchId != null ? branchMap[dto.branchId] ?? '' : '',
    };
}

function emptyModalData(defaultBranchId: number | undefined): ServiceFormData {
    return {
        categoryId: 0,
        branchId: defaultBranchId ?? 0,
        name: '',
        image: null,
        removeExistingImage: false,
        minDuration: '00:30:00',
        maxDuration: '01:00:00',
        serviceType: 3,
        // AB#1847 (PBI-I) — REQUIRED, no default. The owner picks Adult /
        // Kid / Both on first render. `null` here = unchosen; the modal's
        // Zod schema converts via the empty-string sentinel and surfaces
        // the field error.
        ageGroup: null,
        hasHomeService: false,
        description: '',
        isActive: true,
        pricingOptions: [
            { name: 'Standard', price: 0, currency: 'AED', duration: '00:30:00' },
        ],
        preservedStaffIds: [],
    };
}

/**
 * BE returns the ServiceType enum as a JSON string ("MenOnly" / "WomenOnly" /
 * "Everyone"), not the integer the create handler accepts. Normalize to the
 * integer so the form's Zod schema validates and chip-selection comparisons
 * (`field.value === SERVICE_TYPE.WOMEN`) still work.
 */
function normalizeServiceType(raw: number | string | undefined): number {
    if (typeof raw === 'number') return raw;
    switch (raw) {
        case 'MenOnly': return 1;
        case 'WomenOnly': return 2;
        case 'Everyone': return 3;
        default: return 3;
    }
}

function rowToModalData(row: ServiceRow, categories: Category[]): ServiceFormData {
    // B16 — Resolve initial categoryId from row.categoryName so the Edit
    // modal opens with the service's current category pre-selected. BE now
    // accepts an optional CategoryId on update and re-parents when it
    // differs from the current.
    const matchedCategoryId =
        categories.find(c => c.name === row.categoryName)?.id ?? 0;
    return {
        categoryId: matchedCategoryId,
        branchId: row.branchId ?? 0,
        name: row.name,
        image: null,
        removeExistingImage: false,
        minDuration: row.minDuration,
        maxDuration: row.maxDuration,
        serviceType: normalizeServiceType(row.serviceType as unknown as number | string),
        // AB#1847 (PBI-I) — pre-fill from BE. Legacy rows pre-AB#1847 back-
        // fill to "Adult" server-side; if BE returns anything else missing
        // we silently default to Adult (the safest "no info" assumption).
        ageGroup:
            row.ageGroup === 'Adult' || row.ageGroup === 'Kid' || row.ageGroup === 'Both'
                ? row.ageGroup
                : 'Adult',
        hasHomeService: row.hasHomeService,
        description: row.description ?? '',
        isActive: row.isActive,
        pricingOptions:
            row.pricingOptions?.length > 0
                ? row.pricingOptions.map(p => ({
                      name: p.name,
                      price: p.price,
                      currency: p.currency,
                      duration: p.duration,
                  }))
                : [{ name: 'Standard', price: 0, currency: 'AED', duration: '00:30:00' }],
        // PBI 1962 (AB#1968) — seed the option-groups builder from the DTO's
        // groups (present only when the backend flag is ON; additive). Absent ⇒
        // the modal wraps the flat pricing options into a default Variant group.
        existingOptionGroups: row.optionGroups,
        // CR-2026-05-12-v2 — preserve existing staff assignments on edit so
        // dropping the staff step from the modal doesn't wipe what the
        // standalone /services wizard set previously.
        preservedStaffIds: row.assignedStaffIds ?? [],
    };
}

/**
 * CR-2026-05-15-v3 — Read-only "Performed by" row on the service card.
 *
 * Empty list policy:
 *   - !hasHomeService → "Any active staff" (neutral pill). Salons don't need
 *     to pick capability for in-salon walk-in unless they want to narrow it.
 *   - hasHomeService  → amber "Pick staff for home visits" hint. Home service
 *     needs an explicit set because of travel/safety/PII; an empty list at
 *     the time of booking would silently let any staff visit.
 *
 * 1-3 staff: render up to 3 first-name pills.
 * 4+ staff:  render first 3 + "+N more" pill (no menu in this CR — the staff
 *            modal is the source of truth for the full list).
 */
const PerformedByRow: React.FC<{
    staffIds: number[];
    staffNameById: Map<number, string>;
    hasHomeService: boolean;
}> = ({ staffIds, staffNameById, hasHomeService }) => {
    if (!staffIds || staffIds.length === 0) {
        if (hasHomeService) {
            return (
                <div className={s.performedBy}>
                    <span className={s.performedByLabel}>Performed by</span>
                    <span
                        className={`${s.pill} ${s.performedByWarn}`}
                        title="Home-service bookings need an explicit list of staff who can travel."
                    >
                        ⚠ Pick staff for home visits
                    </span>
                </div>
            );
        }
        return (
            <div className={s.performedBy}>
                <span className={s.performedByLabel}>Performed by</span>
                <span
                    className={`${s.pill} ${s.performedByAny}`}
                    title="Open Staff and pick specific staff to narrow."
                >
                    Any active staff
                </span>
            </div>
        );
    }

    const known = staffIds
        .map(id => ({ id, name: staffNameById.get(id) }))
        .filter(x => !!x.name) as { id: number; name: string }[];
    const visible = known.slice(0, 3);
    const moreCount = staffIds.length - visible.length;

    return (
        <div className={s.performedBy}>
            <span className={s.performedByLabel}>Performed by</span>
            {visible.map(x => (
                <span key={x.id} className={`${s.pill} ${s.performedByName}`}>
                    {x.name}
                </span>
            ))}
            {moreCount > 0 && (
                <span className={`${s.pill} ${s.performedByMore}`}>+{moreCount} more</span>
            )}
        </div>
    );
};

/* CR-2026-05-15-v5 — localStorage key for the collapsed-categories set,
   scoped per business so multi-profile owners don't bleed state across salons. */
const collapsedCategoriesStorageKey = (businessId: number | null | undefined) =>
    `devanza:my-business:services:collapsed-categories:${businessId ?? 'anon'}`;

function loadCollapsedCategories(businessId: number | null | undefined): Set<string> {
    try {
        const raw = window.localStorage.getItem(collapsedCategoriesStorageKey(businessId));
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return new Set();
        return new Set(parsed.filter((x): x is string => typeof x === 'string'));
    } catch {
        return new Set();
    }
}

function saveCollapsedCategories(businessId: number | null | undefined, set: Set<string>): void {
    try {
        window.localStorage.setItem(
            collapsedCategoriesStorageKey(businessId),
            JSON.stringify(Array.from(set)),
        );
    } catch {
        /* localStorage full / disabled — graceful no-op */
    }
}

const ServicesSection: React.FC = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    // CR-2026-05-15-v5 — businessId scopes the collapsed-categories localStorage key
    const { user } = useUser();
    const businessId = user?.businessId ?? null;
    const {
        getServices,
        createService,
        updateService,
        deleteService,
        loading,
    } = useServices({ forOwner: true });
    const { getBusinessCategories } = useCategories();
    const { getBranches } = useBranches();
    const { getBusinessStaff } = useStaff();

    const invalidateServicesCache = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.services.list() });
    }, [queryClient]);

    const [rows, setRows] = useState<ServiceRow[] | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [branches, setBranches] = useState<BranchDto[]>([]);
    // CR-2026-05-15-v3 — staff map for resolving the "Performed by" pill row
    // on each service card.
    const [staff, setStaff] = useState<BusinessStaffDto[]>([]);
    const [fetchError, setFetchError] = useState<string | null>(null);

    // CR-2026-05-15-v5 — Collapsed-category set. Persists to localStorage
    // (per business) so the owner doesn't have to re-collapse every navigation.
    // Re-reads when businessId changes (multi-profile switch).
    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() => loadCollapsedCategories(businessId));
    useEffect(() => {
        setCollapsedCategories(loadCollapsedCategories(businessId));
    }, [businessId]);

    const toggleCategoryCollapsed = useCallback((categoryName: string) => {
        setCollapsedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryName)) next.delete(categoryName);
            else next.add(categoryName);
            saveCollapsedCategories(businessId, next);
            return next;
        });
    }, [businessId]);

    const [isAddEditOpen, setIsAddEditOpen] = useState(false);
    const [isAdd, setIsAdd] = useState(true);
    const [editingRow, setEditingRow] = useState<ServiceRow | null>(null);
    const [hasUnsavedModalChanges, setHasUnsavedModalChanges] = useState(false);

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deletingRow, setDeletingRow] = useState<ServiceRow | null>(null);

    const loadAll = useCallback(async () => {
        setFetchError(null);
        try {
            // CR-2026-05-15-v3 — pull staff in parallel so we can resolve
            // assignedStaffIds → fullName for the "Performed by" pill row.
            // Staff is non-critical for rendering services, so its failure
            // shouldn't poison the whole load.
            const [servicesData, catsData, branchesRes, staffRes] = await Promise.all([
                getServices(),
                getBusinessCategories(),
                getBranches(),
                getBusinessStaff({ pageSize: 50 }).catch(() => null),
            ]);
            const activeBranches = (branchesRes?.branches ?? []).filter(b => b.active);
            const map = Object.fromEntries(activeBranches.map(b => [b.id, b.name]));
            setBranches(activeBranches);
            setCategories(catsData?.categories ?? []);
            setStaff(staffRes?.staff ?? []);
            setRows(servicesData.map(svc => toRow(svc, map)));
        } catch (err) {
            const msg = (err as Error)?.message || 'Failed to load services';
            setFetchError(msg);
            if (rows === null) showToast(msg, 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getServices, getBusinessCategories, getBranches, getBusinessStaff, showToast]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    /* ── Modal handlers ─────────────────────────────────────────────── */
    const openAdd = () => {
        // Categories are gated by the standalone /services page (no inline
        // create yet); the empty-state CTA routes there instead of opening
        // the modal — so this guard only catches the "added service, then
        // removed all categories" edge case.
        if (categories.length === 0) {
            showToast(
                'Set up at least one category first — opening the Services page.',
                'info',
            );
            return;
        }
        if (branches.length === 0) {
            showToast(
                'Add an active branch in the Branches section before creating a service.',
                'error',
            );
            return;
        }
        setIsAdd(true);
        setEditingRow(null);
        setHasUnsavedModalChanges(false);
        setIsAddEditOpen(true);
    };

    // Whether prerequisites exist for opening the add modal at all.
    const canCreateService = categories.length > 0 && branches.length > 0;

    const openEdit = (row: ServiceRow) => {
        setIsAdd(false);
        setEditingRow(row);
        setHasUnsavedModalChanges(false);
        setIsAddEditOpen(true);
    };

    const closeAddEdit = () => {
        if (hasUnsavedModalChanges) {
            const ok = window.confirm('Discard unsaved changes?');
            if (!ok) return;
        }
        setIsAddEditOpen(false);
        setHasUnsavedModalChanges(false);
    };

    const handleSave = async (form: ServiceFormData) => {
        try {
            // AB#1847 — Zod refine in the modal guarantees a non-null value
            // before this handler fires; the runtime guard keeps the type
            // narrow for the BE payload and gives us a clean stack trace if
            // a future caller bypasses the modal's validation path.
            if (!form.ageGroup) {
                showToast(`Couldn't ${isAdd ? 'create' : 'update'} service: age group is required.`, 'error');
                return;
            }
            const ageGroup = form.ageGroup;
            if (isAdd) {
                await createService({
                    categoryId: form.categoryId,
                    branchId: form.branchId,
                    name: form.name,
                    image: form.image || undefined,
                    minDuration: form.minDuration,
                    maxDuration: form.maxDuration,
                    serviceType: form.serviceType,
                    ageGroup,
                    hasHomeService: form.hasHomeService,
                    description: form.description,
                    pricingOptions: form.pricingOptions,
                    // PBI 1962 (AB#1968) — additive OptionGroupsJson (undefined
                    // when the builder flag is OFF → backend auto-wraps).
                    optionGroups: form.optionGroups,
                    staffIds: [],
                });
                showToast('Service created.', 'success');
            } else if (editingRow) {
                await updateService(editingRow.id, {
                    branchId: form.branchId,
                    // B16 — Pass categoryId on edit so BE can re-parent the
                    // service when the owner picks a different category.
                    // BE handler no-ops when the value matches current.
                    categoryId: form.categoryId,
                    name: form.name,
                    image: form.image || undefined,
                    removeExistingImage: form.removeExistingImage,
                    minDuration: form.minDuration,
                    maxDuration: form.maxDuration,
                    serviceType: form.serviceType,
                    ageGroup,
                    hasHomeService: form.hasHomeService,
                    description: form.description,
                    isActive: form.isActive,
                    pricingOptions: form.pricingOptions,
                    // PBI 1962 (AB#1968) — additive OptionGroupsJson.
                    optionGroups: form.optionGroups,
                    // CR-2026-05-12-v2 — pass through whatever was already
                    // assigned so the staff drop here doesn't reset it.
                    staffIds: form.preservedStaffIds ?? [],
                });
                showToast('Service updated.', 'success');
            }
            invalidateServicesCache();
            await loadAll();
            setIsAddEditOpen(false);
            setHasUnsavedModalChanges(false);
        } catch (err) {
            showToast(`Couldn't ${isAdd ? 'create' : 'update'} service. Try again.`, 'error');
        }
    };

    const openDeleteFromRow = (row: ServiceRow) => {
        setDeletingRow(row);
        setIsAddEditOpen(false);
        setIsDeleteOpen(true);
    };

    const openDeleteFromModal = () => {
        if (!editingRow) return;
        setDeletingRow(editingRow);
        setIsAddEditOpen(false);
        setIsDeleteOpen(true);
    };

    const handleDelete = async () => {
        if (!deletingRow) return;
        try {
            await deleteService(deletingRow.id);
            showToast('Service deleted.', 'success');
            setIsDeleteOpen(false);
            setDeletingRow(null);
            invalidateServicesCache();
            await loadAll();
        } catch (err) {
            showToast("Couldn't delete service. Try again.", 'error');
        }
    };

    /* ── Filters + search (CR-2026-05-15-v2) ────────────────────────── */
    // URL-persisted filter state: ?search=…&branch=<id>&category=<name>&status=active|inactive
    const [searchParams, setSearchParams] = useSearchParams();
    const urlSearch = searchParams.get('search') ?? '';
    const urlBranchId = searchParams.get('branch') ?? '';
    const urlCategoryName = searchParams.get('category') ?? '';
    const urlStatus = searchParams.get('status') ?? ''; // '', 'active', 'inactive'

    // Local debounced copy of the search input so React Router doesn't re-render
    // the whole tree on every keystroke. Synced to URL via the 200 ms timeout.
    const [searchInput, setSearchInput] = useState(urlSearch);
    useEffect(() => {
        // Keep input in sync if URL changes from outside (browser back/forward).
        setSearchInput(urlSearch);
    }, [urlSearch]);

    // B22 — Sub-tab state for /my-business → Services. Two views:
    //   'manage' — the existing services list (default)
    //   'clone'  — the inline CloneServicesPanel
    // Persisted in URL via ?tab=clone so refresh + share preserve it.
    const activeTab: 'manage' | 'clone' =
        searchParams.get('tab') === 'clone' ? 'clone' : 'manage';
    const setActiveTab = useCallback((tab: 'manage' | 'clone') => {
        const next = new URLSearchParams(searchParams);
        if (tab === 'clone') next.set('tab', 'clone');
        else next.delete('tab');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);
    useEffect(() => {
        const id = window.setTimeout(() => {
            if (searchInput === urlSearch) return;
            const next = new URLSearchParams(searchParams);
            if (searchInput.trim()) next.set('search', searchInput.trim());
            else next.delete('search');
            setSearchParams(next, { replace: true });
            // No-op analytics hook — wire when App Insights lands.
            // trackEvent('services_filter_applied', { filterType: 'search', hasOtherFilters: !!(urlBranchId || urlCategoryName || urlStatus) });
        }, 200);
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    const updateFilter = useCallback(
        (key: 'branch' | 'category' | 'status', value: string) => {
            const next = new URLSearchParams(searchParams);
            if (value) next.set(key, value);
            else next.delete(key);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const clearAllFilters = useCallback(() => {
        setSearchInput('');
        setSearchParams(new URLSearchParams(), { replace: true });
    }, [setSearchParams]);

    const hasActiveFilters =
        !!urlSearch.trim() || !!urlBranchId || !!urlCategoryName || !!urlStatus;

    // B19 — Removed `branchById` lookup; the only caller was the
    // active-filter "Branch: X ✕" chip which got dropped in favor of the
    // pill row's active state. The pills render branch names directly
    // from the `branches` array.

    // CR-2026-05-15-v3 — staffId → first-name (short) for the "Performed by"
    // pill row. We keep it to first name to avoid wrapping the card body when
    // 3 staff names share one line; the full name still shows in tooltips and
    // in the staff modal.
    const staffNameById = useMemo(() => {
        const m = new Map<number, string>();
        for (const s of staff) {
            const first = (s.fullName ?? '').trim().split(/\s+/)[0] || s.fullName || '';
            m.set(s.id, first);
        }
        return m;
    }, [staff]);

    // Unique non-empty category names available to filter by — taken from the
    // services we actually have, not from the master list, so the dropdown
    // never offers an empty bucket.
    const availableCategories = useMemo(() => {
        const set = new Set<string>();
        for (const r of rows ?? []) {
            if (r.categoryName) set.add(r.categoryName);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const filteredRows = useMemo(() => {
        if (!rows) return null;
        const q = urlSearch.trim().toLowerCase();
        const branchIdNum = urlBranchId ? parseInt(urlBranchId, 10) : null;
        return rows.filter(r => {
            if (q) {
                const name = (r.name || '').toLowerCase();
                const desc = (r.description || '').toLowerCase();
                if (!name.includes(q) && !desc.includes(q)) return false;
            }
            if (branchIdNum != null && r.branchId !== branchIdNum) return false;
            if (urlCategoryName && r.categoryName !== urlCategoryName) return false;
            if (urlStatus === 'active' && !r.isActive) return false;
            if (urlStatus === 'inactive' && r.isActive) return false;
            return true;
        });
    }, [rows, urlSearch, urlBranchId, urlCategoryName, urlStatus]);

    /* ── Derived view state ─────────────────────────────────────────── */
    const isInitialLoading = loading && rows === null;
    const showEmpty = !isInitialLoading && !fetchError && rows !== null && rows.length === 0;
    const showList = !isInitialLoading && !fetchError && rows !== null && rows.length > 0;

    const activeCount = useMemo(
        () => (rows ?? []).filter(r => r.isActive).length,
        [rows],
    );

    /**
     * Grouping policy (CR-2026-05-15-v2 AC-5): when ANY filter is active we
     * collapse the category headings into a flat name-sorted list. When all
     * filters are clear we restore the category-grouped view.
     */
    const groupedRows = useMemo(() => {
        if (!filteredRows) return [];
        if (hasActiveFilters) {
            const flat = [...filteredRows].sort((a, b) => a.name.localeCompare(b.name));
            return [{ category: '', items: flat, flat: true as const }];
        }
        const groups = new Map<string, ServiceRow[]>();
        for (const row of filteredRows) {
            const key = row.categoryName || 'Uncategorized';
            const list = groups.get(key) ?? [];
            list.push(row);
            groups.set(key, list);
        }
        return Array.from(groups.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, items]) => ({ category, items, flat: false as const }));
    }, [filteredRows, hasActiveFilters]);

    return (
        <div>
            <header className={styles.header}>
                <h1>Services</h1>
                {/* B18 — Dropped the explainer subtitle ("The services your
                    salon offers — haircuts…") per founder feedback. The h1
                    + the contextual filter pills below already make the
                    surface self-evident; the marketing-style explainer
                    was eating vertical space on every visit. */}
            </header>

            {/* B22 — Sub-tab strip: Manage (services list) | Clone (inline
                clone flow). Replaces the previous "Copy services to another
                branch" button that opened a popup. The clone flow now lives
                in page space so users see source + destination + service
                grid + clone CTA all in one place. */}
            {branches.length >= 2 && (
                <div className={s.subTabs} role="tablist" aria-label="Services view">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'manage'}
                        className={`${s.subTab} ${activeTab === 'manage' ? s.subTabActive : ''}`}
                        onClick={() => setActiveTab('manage')}
                    >
                        Manage services
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'clone'}
                        className={`${s.subTab} ${activeTab === 'clone' ? s.subTabActive : ''}`}
                        onClick={() => setActiveTab('clone')}
                    >
                        Clone services across branches
                    </button>
                </div>
            )}

            {/* B22 — Clone tab renders the inline CloneServicesPanel and
                short-circuits the rest of the page (no list, no filters,
                no marketplace hint). The user is in clone-mode and the
                visual context should be focused on that single task. */}
            {activeTab === 'clone' ? (
                <CloneServicesPanel
                    allServices={rows ?? []}
                    branches={branches}
                    onCloned={() => {
                        invalidateServicesCache();
                        // Trigger refetch in the local list state too.
                        setRows(null);
                    }}
                />
            ) : (
            <>

            {/* B18 + B24 — Marketplace-listing hint banner. Onboarding
                nudge only: once the salon has hit ACTIVATION_THRESHOLD
                active services, marketplace eligibility is no longer
                pending and the banner becomes permanent decoration
                instead of an actionable prompt. Hide it post-activation
                so /my-business → Services compacts vertically once the
                owner is past setup. The dedicated `.progressCard`
                further down handles the < threshold case with a
                progress bar; this banner is the "you haven't started
                yet" prompt that fires when activeCount === 0 (the
                empty-state path mostly catches this anyway, but the
                populated-with-inactive case still benefits). */}
            {activeCount < ACTIVATION_THRESHOLD && (
            <div className={styles.intro}>
                <span className={styles.introIcon}>💡</span>
                <div>
                    <strong>Three published services unlocks your marketplace listing.</strong>{' '}
                    Customers search Devanza by service — the more you offer, the more
                    visibility you get.
                </div>
            </div>
            )}

            {/* Loading skeleton */}
            {isInitialLoading && (
                <ul className={s.cardList} aria-busy="true">
                    {[0, 1, 2].map(i => (
                        <li key={i} className={s.skeletonCard}>
                            <div className={`${s.skeletonLine} ${s.skeletonLineMed}`} />
                            <div className={`${s.skeletonLine} ${s.skeletonLineLong}`} />
                            <div className={`${s.skeletonLine} ${s.skeletonLineShort}`} />
                        </li>
                    ))}
                </ul>
            )}

            {/* Error banner with retry */}
            {fetchError && rows === null && (
                <div className={styles.intro} role="alert">
                    <span className={styles.introIcon}>⚠️</span>
                    <div>
                        <strong>Couldn't load your services.</strong> {fetchError}{' '}
                        <button
                            type="button"
                            onClick={loadAll}
                            className={styles.btn}
                            style={{ marginInlineStart: 12 }}
                        >
                            Try again
                        </button>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {showEmpty && (
                <div className={s.empty}>
                    <div className={s.emptyIcon} aria-hidden="true">💼</div>
                    {canCreateService ? (
                        <>
                            <h2 className={s.emptyTitle}>You haven't added any services yet</h2>
                            <p className={s.emptyDesc}>
                                Tell customers what you offer. Each service gets its own price,
                                duration, and an optional photo. Three or more published services
                                unlock your Devanza marketplace listing.
                            </p>
                            <button type="button" className={styles.btnPrimary} onClick={openAdd}>
                                + Add your first service
                            </button>
                        </>
                    ) : categories.length === 0 ? (
                        <>
                            <h2 className={s.emptyTitle}>Set up your service categories first</h2>
                            <p className={s.emptyDesc}>
                                Categories (Hair, Nails, Massage, etc.) organize your service
                                catalog and help customers filter on the marketplace. We'll walk
                                you through it on the Categories page — pick what fits your
                                salon, then come back here to add the services under each one.
                            </p>
                            {/* CR-2026-05-15 — Routes to the new My Business → Categories
                                sub-section (not the standalone /services Categories tab,
                                which now shows a soft-deprecate banner pointing here). */}
                            <Link to="/my-business/categories" className={styles.btnPrimary}>
                                + Set up categories first
                            </Link>
                        </>
                    ) : (
                        <>
                            <h2 className={s.emptyTitle}>Add a branch before your first service</h2>
                            <p className={s.emptyDesc}>
                                Every service lives at a branch — that's how Devanza routes
                                bookings to the right location. Add at least one active branch,
                                then come back to add your services.
                            </p>
                            <Link to="/my-business/branches" className={styles.btnPrimary}>
                                + Add a branch
                            </Link>
                        </>
                    )}
                </div>
            )}

            {/* Populated list */}
            {showList && (
                <>
                    {/* CR-2026-05-15-v5 — per-branch quick tabs. Only render when
                        the salon has ≥2 branches; single-branch salons see no
                        clutter. Tabs are syntactic sugar over the existing
                        ?branch= URL filter — clicking a tab sets the same param
                        that the Branch filter chip below uses. They stay in
                        sync because both read/write the same URL state. */}
                    {branches.length >= 2 && (
                        <div className={s.branchTabs} role="tablist" aria-label="Filter by branch">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={!urlBranchId}
                                className={`${s.branchTab} ${!urlBranchId ? s.branchTabActive : ''}`}
                                onClick={() => updateFilter('branch', '')}
                            >
                                All branches
                                <span className={s.branchTabCount}>
                                    ({rows!.length})
                                </span>
                            </button>
                            {branches.map(b => {
                                const branchCount = rows!.filter(r => r.branchId === b.id).length;
                                const isActive = urlBranchId === String(b.id);
                                return (
                                    <button
                                        key={b.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        className={`${s.branchTab} ${isActive ? s.branchTabActive : ''}`}
                                        onClick={() => updateFilter('branch', String(b.id))}
                                    >
                                        {b.name}
                                        <span className={s.branchTabCount}>({branchCount})</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* B24 — Filter row consolidates four bars into one:
                        count meta on the left, search + category + status
                        in the middle, "+ Add service" on the right. The
                        previous standalone .toolbar (count line + Add) was
                        merged in. Below this, the active-filters pills
                        (e.g., Search: "wax") still render only when there's
                        something to clear. */}
                    <div className={s.stickyFilters}>
                    <div className={s.filterRow}>
                        <div className={s.toolbarMeta}>
                            <span className={s.count}>
                                {hasActiveFilters
                                    ? `${filteredRows!.length} of ${rows!.length}`
                                    : `${rows!.length} ${rows!.length === 1 ? 'service' : 'services'}`}
                            </span>
                            <span className={s.toolbarMetaSub}>
                                {activeCount} active · {rows!.length - activeCount} inactive
                            </span>
                        </div>
                        <div className={s.searchWrap}>
                            <span className={s.searchIcon} aria-hidden="true">🔍</span>
                            <input
                                type="search"
                                className={s.searchInput}
                                placeholder="Search services by name…"
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                                aria-label="Search services"
                            />
                        </div>
                        <div className={s.filterChips}>
                            {/* B19 — Branch dropdown removed. The top-row pill
                                tabs (rendered above) ARE the branch filter and
                                already show counts + active state. Two controls
                                wired to the same updateFilter('branch') was
                                duplicated UX. Pills scale via horizontal scroll
                                when a business has many branches. */}
                            <label className={s.filterChip}>
                                <span className={s.filterChipLabel}>Category</span>
                                <select
                                    value={urlCategoryName}
                                    onChange={e => updateFilter('category', e.target.value)}
                                    aria-label="Filter by category"
                                >
                                    <option value="">All</option>
                                    {availableCategories.map(name => (
                                        <option key={name} value={name}>
                                            {name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className={s.filterChip}>
                                <span className={s.filterChipLabel}>Status</span>
                                <select
                                    value={urlStatus}
                                    onChange={e => updateFilter('status', e.target.value)}
                                    aria-label="Filter by status"
                                >
                                    <option value="">All</option>
                                    <option value="active">Active only</option>
                                    <option value="inactive">Inactive only</option>
                                </select>
                            </label>
                        </div>
                        {/* B24 — Primary "+ Add service" CTA moved into the
                            filter row alongside the count meta on the left
                            and the filters in the middle. Eliminates the
                            separate toolbar row above and tightens vertical
                            stacking on /my-business → Services. */}
                        <button
                            type="button"
                            className={`${styles.btnPrimary} ${s.addServiceBtn}`}
                            onClick={openAdd}
                        >
                            + Add service
                        </button>
                    </div>

                    {/* Active filter pills + Clear all */}
                    {hasActiveFilters && (
                        <div className={s.activeFiltersBar}>
                            {urlSearch && (
                                <span className={s.activeFilterPill}>
                                    Search: "{urlSearch}"
                                    <button
                                        type="button"
                                        className={s.activeFilterPillX}
                                        onClick={() => {
                                            setSearchInput('');
                                            const next = new URLSearchParams(searchParams);
                                            next.delete('search');
                                            setSearchParams(next, { replace: true });
                                        }}
                                        aria-label="Clear search filter"
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                            {/* B19 — Branch "active filter" chip removed.
                                The active pill tab already shows which branch
                                you're on (highlighted state + count); adding
                                a redundant "Branch: X ✕" chip down here was
                                noise. To clear branch filter, click "All
                                branches" in the pill row. */}
                            {urlCategoryName && (
                                <span className={s.activeFilterPill}>
                                    Category: {urlCategoryName}
                                    <button
                                        type="button"
                                        className={s.activeFilterPillX}
                                        onClick={() => updateFilter('category', '')}
                                        aria-label="Clear category filter"
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                            {urlStatus && (
                                <span className={s.activeFilterPill}>
                                    Status: {urlStatus === 'active' ? 'Active only' : 'Inactive only'}
                                    <button
                                        type="button"
                                        className={s.activeFilterPillX}
                                        onClick={() => updateFilter('status', '')}
                                        aria-label="Clear status filter"
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                            <button
                                type="button"
                                className={s.clearAllLink}
                                onClick={clearAllFilters}
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                    </div>{/* end .stickyFilters (CR-v5) */}

                    {/* Marketplace-eligibility hint while below the threshold */}
                    {activeCount > 0 && activeCount < ACTIVATION_THRESHOLD && (
                        <div className={s.progressCard}>
                            <div className={s.progressIcon} aria-hidden="true">✨</div>
                            <div className={s.progressBody}>
                                <div className={s.progressTitle}>
                                    {ACTIVATION_THRESHOLD - activeCount}{' '}
                                    {ACTIVATION_THRESHOLD - activeCount === 1
                                        ? 'more service'
                                        : 'more services'}{' '}
                                    until your marketplace listing goes live
                                </div>
                                <div className={s.progressDesc}>
                                    Publish at least {ACTIVATION_THRESHOLD} active services and your
                                    salon becomes discoverable to customers searching Devanza.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CR-2026-05-15-v2 AC-8 — filter-empty-state when filters
                        yield zero. Distinct from "no services at all". */}
                    {hasActiveFilters && filteredRows!.length === 0 && (
                        <div className={s.filterEmpty}>
                            <span aria-hidden="true">🔎</span>
                            <span>No services match these filters.</span>
                            <button
                                type="button"
                                className={s.filterEmptyAction}
                                onClick={clearAllFilters}
                            >
                                Clear all
                            </button>
                        </div>
                    )}

                    {/* Grouped by category — collapses to a flat list when any
                        filter is active (AC-5).
                        CR-2026-05-15-v5 — headings are clickable to collapse /
                        expand the group at scale; state persists in localStorage
                        per business. */}
                    {groupedRows.map(group => {
                        const isCollapsed = !group.flat && collapsedCategories.has(group.category);
                        return (
                        <React.Fragment key={group.flat ? '__flat' : group.category}>
                            {!group.flat && (
                                <button
                                    type="button"
                                    className={`${s.groupHeading} ${s.groupHeadingButton} ${isCollapsed ? s.groupHeadingCollapsed : ''}`}
                                    onClick={() => toggleCategoryCollapsed(group.category)}
                                    aria-expanded={!isCollapsed}
                                    aria-controls={`category-group-${group.category}`}
                                >
                                    <span className={s.groupChevron} aria-hidden="true">
                                        {isCollapsed ? '▶' : '▼'}
                                    </span>
                                    <span>{group.category}</span>
                                    <span className={s.groupHeadingCount}>({group.items.length})</span>
                                </button>
                            )}
                            {isCollapsed ? null : (
                            <ul className={s.cardList} id={!group.flat ? `category-group-${group.category}` : undefined}>
                                {group.items.map(row => (
                                    <li key={row.id} className={s.card}>
                                        <div className={s.cardThumb}>
                                            {row.imageUrl ? (
                                                <img
                                                    src={resolveImageUrl(row.imageUrl)}
                                                    alt=""
                                                />
                                            ) : (
                                                <span aria-hidden="true">💼</span>
                                            )}
                                        </div>
                                        <div className={s.cardMain}>
                                            <div className={s.cardNameRow}>
                                                <span className={s.cardName}>{row.name}</span>
                                                {/* AB#1840 (PBI-B) / AB#1838 — per-service gender chip.
                                                    Renders next to the service name on every service-list
                                                    surface (here = My Business preview). Mixed/Everyone
                                                    is rendered explicitly per founder Q6=A. RTL handled
                                                    inside the chip via logical properties. */}
                                                <GenderGlyph serviceType={row.serviceType} />
                                                {/* AB#1847 (PBI-I) / AB#1838 — per-service age-group chip.
                                                    Renders next to the GenderGlyph. Adult (the default)
                                                    renders nothing to keep the row visually quiet — only
                                                    Kid and Both surface as chips. AR `Both` = `كلاهما`
                                                    per founder Q1 (NOT `الجميع`). */}
                                                <AgeGroupChip ageGroup={row.ageGroup} />
                                                <span
                                                    className={`${s.pill} ${row.isActive ? s.statusActive : s.statusInactive}`}
                                                >
                                                    {row.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                                {row.hasHomeService && (
                                                    <span className={`${s.pill} ${s.homePill}`}>
                                                        🏠 Home service
                                                    </span>
                                                )}
                                                {row.branchName && (
                                                    <span className={`${s.pill} ${s.branchPill}`}>
                                                        {row.branchName}
                                                    </span>
                                                )}
                                            </div>
                                            <div className={s.cardMeta}>
                                                <span>{row.formattedDuration}</span>
                                                <span className={s.cardMetaSep}>·</span>
                                                <span>{row.formattedLowestPrice}</span>
                                                {row.pricingOptions?.length > 1 && (
                                                    <>
                                                        <span className={s.cardMetaSep}>·</span>
                                                        <span>
                                                            {row.pricingOptions.length} pricing
                                                            options
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            {/* CR-2026-05-15-v3 — "Performed by" row. Read-only:
                                                staff is picked in the staff modal, not here. */}
                                            <PerformedByRow
                                                staffIds={row.assignedStaffIds ?? []}
                                                staffNameById={staffNameById}
                                                hasHomeService={row.hasHomeService}
                                            />
                                            {row.description && (
                                                <div className={s.cardDescription}>
                                                    {row.description}
                                                </div>
                                            )}
                                        </div>
                                        <div className={s.cardActions}>
                                            <button
                                                type="button"
                                                className={s.iconBtn}
                                                onClick={() => openEdit(row)}
                                                aria-label={`Edit ${row.name}`}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className={`${s.iconBtn} ${s.iconBtnDanger}`}
                                                onClick={() => openDeleteFromRow(row)}
                                                aria-label={`Delete ${row.name}`}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            )}
                        </React.Fragment>
                        );
                    })}
                </>
            )}

            {/* B22 — close the Manage-tab content fragment opened
                above (right after the sub-tab strip). */}
            </>
            )}

            {isAddEditOpen && (
                <ServiceAddEditModal
                    isOpen={isAddEditOpen}
                    onClose={closeAddEdit}
                    onSave={handleSave}
                    initialData={
                        editingRow
                            ? rowToModalData(editingRow, categories)
                            : emptyModalData(branches[0]?.id)
                    }
                    isAdd={isAdd}
                    loading={loading}
                    onDirtyChange={setHasUnsavedModalChanges}
                    onDeleteClicked={openDeleteFromModal}
                    categories={categories}
                    branches={branches}
                    existingImageUrl={editingRow?.imageUrl}
                    // γ-lite — feed the picker every active service across the
                    // business so owners can copy from any branch when adding
                    // a new one. The picker only renders in Add mode.
                    existingServices={(rows ?? []).filter(r => r.isActive)}
                />
            )}

            {/* B22 — BulkCloneServicesModal mount removed. The clone
                flow now lives in an inline sub-tab via CloneServicesPanel
                (rendered in-place above the services list when the
                "Clone" tab is active). */}

            {isDeleteOpen && (
                <DeleteModal
                    isOpen={isDeleteOpen}
                    onClose={() => {
                        setIsDeleteOpen(false);
                        setDeletingRow(null);
                    }}
                    onDelete={handleDelete}
                    branchName={deletingRow?.name || ''}
                />
            )}
        </div>
    );
};

export default ServicesSection;
